<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="700" persistent scrollable>
    <v-card>
      <v-card-title class="teal darken-1 white--text">
        <v-icon left dark>mdi-image-multiple</v-icon>
        Gestión de Imágenes
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle class="pt-3">
        Post: <strong>{{ postTitle }}</strong>
      </v-card-subtitle>

      <v-card-text>
        <!-- Sección subir imagen -->
        <v-card outlined class="pa-3 mb-4">
          <p class="subtitle-2 mb-2">Agregar imagen</p>
          <v-row align="center">
            <v-col cols="12" sm="6">
              <v-file-input v-model="selectedFile" label="Seleccionar imagen" accept="image/*"
                prepend-icon="mdi-camera" show-size dense hide-details></v-file-input>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="captionUpload" label="Descripción (opcional)"
                dense hide-details outlined></v-text-field>
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn color="teal" dark :loading="uploading" :disabled="!selectedFile"
                @click="subirImagen">
                <v-icon left>mdi-upload</v-icon> Subir
              </v-btn>
            </v-col>
          </v-row>
          <v-alert v-if="uploadMsg" :type="uploadMsgType" dense text class="mt-2 mb-0">
            {{ uploadMsg }}
          </v-alert>
        </v-card>

        <!-- Alerta informativa -->
        <v-alert type="info" dense text class="mb-3">
          Si sube una imagen con el mismo nombre, se reemplazará automáticamente.
        </v-alert>

        <!-- Tabla de imágenes cargadas -->
        <v-simple-table v-if="images.length" fixed-header>
          <template v-slot:default>
            <thead>
              <tr>
                <th>#</th>
                <th>Preview</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(img, index) in images" :key="img.id">
                <td>{{ index + 1 }}</td>
                <td>
                  <v-img :src="getImageUrl(img.img_url)" width="60" height="60" contain
                    class="rounded"></v-img>
                </td>
                <td>{{ img.img_url }}</td>
                <td>
                  <span class="caption">{{ img.caption || '—' }}</span>
                </td>
                <td>
                  <v-btn icon color="primary" @click="abrirEdicion(img)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="red" :loading="deletingId === img.id"
                    @click="eliminarImagen(img)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-alert v-else type="warning" dense text>
          Este post no tiene imágenes cargadas.
        </v-alert>
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cerrar">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog editar imagen -->
    <v-dialog v-model="dialogEdit" max-width="500" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-pencil</v-icon>
          Editar Imagen
        </v-card-title>
        <v-card-text class="pt-4">
          <!-- Preview actual -->
          <v-img v-if="editImg" :src="getImageUrl(editImg.img_url)" max-height="200" contain
            class="rounded grey lighten-3 mb-4"></v-img>

          <!-- Descripción -->
          <v-text-field v-model="editCaption" label="Descripción" outlined dense
            placeholder="Agregar una descripción a la imagen"></v-text-field>

          <!-- Reemplazar imagen (opcional) -->
          <v-file-input v-model="editFile" label="Reemplazar imagen (opcional)"
            accept="image/*" prepend-icon="mdi-camera" show-size dense
            hint="Dejar vacío para mantener la imagen actual" persistent-hint></v-file-input>

          <v-alert v-if="editMsg" :type="editMsgType" dense text class="mt-3 mb-0">
            {{ editMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cerrarEdicion">Cancelar</v-btn>
          <v-btn color="teal" dark :loading="editSaving" @click="guardarEdicion">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import forumService from '@/services/forumService.js';
import Compressor from 'compressorjs';

export default {
  name: 'ForumPostImagesModal',
  props: {
    value: { type: Boolean, default: false },
    postId: { type: [Number, String], default: null },
    postTitle: { type: String, default: '' }
  },
  data() {
    return {
      images: [],
      selectedFile: null,
      captionUpload: '',
      uploading: false,
      uploadMsg: '',
      uploadMsgType: 'success',
      deletingId: null,
      // Edit dialog
      dialogEdit: false,
      editImg: null,
      editCaption: '',
      editFile: null,
      editSaving: false,
      editMsg: '',
      editMsgType: 'success'
    };
  },
  watch: {
    value(val) {
      if (val && this.postId) {
        this.cargarImagenes();
        this.uploadMsg = '';
        this.selectedFile = null;
      }
    }
  },
  methods: {
    cargarImagenes() {
      forumService.getPost(this.postId).then(res => {
        this.images = res.data.images || [];
      });
    },
    getImageUrl(filename) {
      return forumService.getImageUrl(this.postId, filename);
    },
    subirImagen() {
      if (!this.selectedFile) return;
      this.uploading = true;
      this.uploadMsg = '';

      this.compressImage(this.selectedFile).then(compressed => {
        const fd = new FormData();
        fd.append('image', compressed);
        if (this.captionUpload) {
          fd.append('caption', this.captionUpload);
        }

        forumService.uploadSingleImage(this.postId, fd).then(res => {
          const replaced = res.data.replaced;
          this.uploadMsg = replaced ? 'Imagen reemplazada exitosamente' : 'Imagen subida exitosamente';
          this.uploadMsgType = 'success';
          this.selectedFile = null;
          this.captionUpload = '';
          this.cargarImagenes();
        }).catch(err => {
          const errors = err.response && err.response.data && err.response.data.errors;
          this.uploadMsg = errors ? errors.join(', ') : 'Error al subir imagen';
          this.uploadMsgType = 'error';
        }).finally(() => {
          this.uploading = false;
        });
      });
    },
    eliminarImagen(img) {
      if (!confirm('¿Eliminar la imagen "' + img.img_url + '"? Se borrará del servidor.')) return;
      this.deletingId = img.id;

      forumService.deleteImage(this.postId, img.id).then(() => {
        this.images = this.images.filter(i => i.id !== img.id);
        this.uploadMsg = 'Imagen eliminada';
        this.uploadMsgType = 'success';
      }).catch(() => {
        this.uploadMsg = 'Error al eliminar imagen';
        this.uploadMsgType = 'error';
      }).finally(() => {
        this.deletingId = null;
      });
    },
    guardarCaption(img) {
      forumService.updateImage(this.postId, img.id, { caption: img.caption || '' }).then(() => {
        // Guardado silencioso al salir del campo
      }).catch(() => {
        this.uploadMsg = 'Error al guardar descripción';
        this.uploadMsgType = 'error';
      });
    },
    abrirEdicion(img) {
      this.editImg = img;
      this.editCaption = img.caption || '';
      this.editFile = null;
      this.editMsg = '';
      this.dialogEdit = true;
    },
    cerrarEdicion() {
      this.dialogEdit = false;
      this.editImg = null;
      this.editFile = null;
      this.editMsg = '';
    },
    guardarEdicion() {
      if (!this.editImg) return;
      this.editSaving = true;
      this.editMsg = '';

      if (this.editFile) {
        // Reemplazar imagen (comprimir + subir con mismo nombre) + actualizar caption
        this.compressImage(this.editFile).then(compressed => {
          const fd = new FormData();
          fd.append('image', compressed);
          fd.append('caption', this.editCaption || '');

          forumService.uploadSingleImage(this.postId, fd).then(() => {
            this.editMsg = 'Imagen reemplazada y descripción actualizada';
            this.editMsgType = 'success';
            this.cargarImagenes();
            setTimeout(() => { this.cerrarEdicion(); }, 800);
          }).catch(() => {
            this.editMsg = 'Error al reemplazar imagen';
            this.editMsgType = 'error';
          }).finally(() => {
            this.editSaving = false;
          });
        });
      } else {
        // Solo actualizar caption
        forumService.updateImage(this.postId, this.editImg.id, { caption: this.editCaption || '' }).then(() => {
          this.editImg.caption = this.editCaption;
          this.editMsg = 'Descripción actualizada';
          this.editMsgType = 'success';
          setTimeout(() => { this.cerrarEdicion(); }, 800);
        }).catch(() => {
          this.editMsg = 'Error al guardar descripción';
          this.editMsgType = 'error';
        }).finally(() => {
          this.editSaving = false;
        });
      }
    },
    compressImage(file) {
      return new Promise((resolve) => {
        new Compressor(file, {
          quality: 0.7,
          maxWidth: 1280,
          convertSize: 500000,
          success: (compressedBlob) => {
            const name = file.name.replace(/\.(jpe?g|png|jfif|webp)$/i, '') + '.jpg';
            const compressedFile = new File([compressedBlob], name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          error: (err) => {
            console.error('Error al comprimir imagen:', err.message);
            resolve(file); // fallback: enviar original
          },
        });
      });
    },
    cerrar() {
      this.$emit('input', false);
      this.$emit('updated');
    }
  }
};
</script>
